import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../../store/hooks';
import { selectSignup } from '../../store/signupSlice';
import { marketingApi } from '../../api/marketing';

const AwaitingVerificationPage: FC = () => {
  const navigate = useNavigate();
  const { email } = useAppSelector(selectSignup);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  const steps = [
    { label: 'Account Created', completed: true },
    { label: 'Information Submitted', completed: true },
    { label: 'Payment Received', completed: true },
    { label: 'Verification Call', completed: status === 'approved' },
    { label: 'Account Activated', completed: status === 'approved' },
  ];

  useEffect(() => {
    checkStatus();
    // Poll for status updates every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (status === 'approved') {
      setTimeout(() => navigate('/welcome'), 2000);
    }
  }, [status, navigate]);

  const checkStatus = async () => {
    try {
      const response = await marketingApi.getStatus();
      setStatus(response.status);
    } catch (err) {
      console.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: status === 'approved' ? 'success.main' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          {status === 'approved' ? (
            <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
          ) : (
            <ScheduleIcon sx={{ fontSize: 48, color: 'white' }} />
          )}
        </Box>

        <Typography variant="h4" gutterBottom fontWeight={600}>
          {status === 'approved'
            ? 'Account Activated!'
            : 'Verification in Progress'}
        </Typography>

        <Typography color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
          {status === 'approved'
            ? "Your Debtornator instance is ready! You'll be redirected shortly."
            : 'Our team is reviewing your application. We typically complete verifications within 1-2 business days.'}
        </Typography>
      </Box>

      {/* Progress Stepper */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Stepper alternativeLabel>
            {steps.map((step, index) => (
              <Step key={index} completed={step.completed}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* What's Next */}
      {status !== 'approved' && (
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              What Happens Next?
            </Typography>

            <List>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Verification Call"
                  secondary="A member of our team will contact you within 1-2 business days to verify your information and answer any questions."
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Account Activation"
                  secondary="Once verified, you'll receive an email with login credentials and your custom subdomain."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary">
              We'll contact you at:
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {email}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Contact Support */}
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Need Help?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            If you have questions or need to expedite your verification, contact our sales team.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              href="mailto:sales@debtornator.com"
            >
              Email Sales
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              href="tel:+18005551234"
            >
              Call Us
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AwaitingVerificationPage;
